import React, { CSSProperties, FocusEvent, FocusEventHandler, forwardRef, InputHTMLAttributes, memo, useCallback, useEffect, useMemo, useState } from 'react';
import CalculatorIcon from '../../../assets/images/calculator-solid-new.svg';
import { InputBase } from 'component-library/index';
import { Button, Row, Col, Title, Image  } from '../../../importCL';
import { cleanNumericInput, currencyOptions, toMonthlyIncome, MAX_ANNUAL_INCOME, toAnnualIncome, MAX_MONTHLY_INCOME } from '../../../utils/income-utils';

export interface IIncomeCalculatorProps extends InputHTMLAttributes<HTMLInputElement> {
    showModal?: boolean;
    monthlyIncome?: string;
    setMonthlyIncome?: React.Dispatch<React.SetStateAction<string>>;
    calculatorValue?: string,
    setCalculatorValue?: React.Dispatch<React.SetStateAction<string>>,
    closeCallback?: (calculatorValue: string) => void;
}

export const useIncomeCalculatorHandling = ({
    monthlyIncomeRef,
    monthlyIncome,
    calculatorValue,
    setCalculatorValue,
    setShowDesc,
    setShowModal,
    handleMonthlyIncomeBlur,
    convertValue,
}) => {
    const handleOpenCalculator = useCallback(() => {
        const cleanedValue = +cleanNumericInput(monthlyIncome);
        if (cleanedValue && cleanedValue > 0) {
            const nextAnnualIncome = toAnnualIncome(cleanedValue).toLocaleString('en-US', currencyOptions);
            setCalculatorValue(nextAnnualIncome);
        } else {
            setCalculatorValue('0');
        }
        setShowModal(true);
    }, [
        setCalculatorValue,
        monthlyIncome,
        setShowModal,
    ]); 

    const closeCalculatorCallback = useCallback(() => {
        handleMonthlyIncomeBlur({ target: { value: convertValue(calculatorValue, 'yearly') } });
        setShowModal(false);
        setShowDesc(true);
        monthlyIncomeRef?.current?.focus();
    }, [
        convertValue,
        setShowModal,
        setShowDesc,
        handleMonthlyIncomeBlur,
    ]);

    return {
        handleOpenCalculator,
        closeCalculatorCallback,
    };
};

export const IncomeCalculator = ({
    monthlyIncome,
    setMonthlyIncome,
    calculatorValue,
    setCalculatorValue,
    closeCallback,
    ...props
}: IIncomeCalculatorProps) => {
    const [ hasRendered, setHasRendered ] = useState(false);


    const modalStyles: CSSProperties = useMemo(() => ({ 
        transition: 'all 0.3s ease-in-out', 
        visibility: hasRendered ? 'visible' : 'hidden', 
        opacity: hasRendered ? 1 : 0,
    }), [ hasRendered ]);

    const inputFocusCallback = useCallback((event: FocusEvent<HTMLInputElement>) => {
        if (event.target.value === '0') {
            setCalculatorValue('');
        }
    }, [ setCalculatorValue ]);

    const inputBlurCallback = useCallback((event: FocusEvent<HTMLInputElement>) => {
        const numericValue = parseInt(cleanNumericInput(event.target.value));
        if (isNaN(numericValue) || numericValue === 0) {
            setCalculatorValue('');
        } else {
            const formattedAnnualIncome = numericValue.toLocaleString('en-US', currencyOptions);
            setCalculatorValue(formattedAnnualIncome);
        }
    }, [ cleanNumericInput, setCalculatorValue ]);

    const handleSetValue = useCallback((val: string) => {
        const cleanedVal = cleanNumericInput(val);
        const numericVal = parseInt(cleanedVal);
        if (cleanedVal && /^0+$/.test(cleanedVal)) {
            return;
        }
        // Limit to max annual income of 1,000,000
        if (isNaN(numericVal) || numericVal <= MAX_ANNUAL_INCOME) {
            const formattedVal = cleanedVal ? numericVal.toLocaleString('en-US', currencyOptions) : '';
            setCalculatorValue(formattedVal);
        }
    }, [ cleanNumericInput, setCalculatorValue ]);

    const gracefulClose = useCallback(() => {
        setHasRendered(false);
        setTimeout(closeCallback, 250); // Animation timing
    }, [ setHasRendered, closeCallback ]);

    useEffect(() => {
        setHasRendered(true);
    }, [ setHasRendered ]);

    return (
        <div className='MonthlyIncome__incomeModal' 
            style={modalStyles}
            tabIndex={-1}>
            <div className='MonthlyIncome__dummyWindow' onClick={gracefulClose}></div>
            <div className='MonthlyIncome__modalWindow'>
                <Title className='MonthlyIncome__close' onClick={gracefulClose}>×</Title>
                <Row className='MonthlyIncome__row'>
                    <Col className='MonthlyIncome__col'>
                        <p className='MonthlyIncome__header'>Annual to Monthly Income Calculator</p>
                        <p className='MonthlyIncome__desc'>Please enter your total annual income and we will convert it to the monthly amount (rounded to the nearest dollar).</p>
                        <InputBase noErrorsOrHelper
                            className='MonthlyIncome__annualInput'
                            type='tel'
                            label='Total Annual Income'
                            {...props}
                            inputClass='pl-12'
                            inputChild={<span className='MonthlyIncome__inputPrefix' aria-hidden={true} tabIndex={-1}>$</span>}
                            value={calculatorValue}
                            setValue={handleSetValue}
                            maxLength={9} // 7x digits + 1x '$' + 1x ','
                            onFocus={inputFocusCallback}
                            onBlur={inputBlurCallback}
                            onPaste={(e: any) => e.preventDefault()}
                            autoComplete='off'
                        />
                        <p className={`MonthlyIncome__modalParagraph ${Number(cleanNumericInput(calculatorValue)) === 0 ? 'MonthlyIncome__inactive' : ''}`}>Your Total Monthly Income<br></br>
                            <span className='MonthlyIncome__modalParagraph--num'>{`$${toMonthlyIncome(+cleanNumericInput(calculatorValue)).toLocaleString('en-US', currencyOptions)}`}</span>
                        </p>
                        <Button size='large' className='MonthlyIncome__button' variant='solid' onClick={gracefulClose} disabled={Number(cleanNumericInput(calculatorValue)) === 0}>Use This Amount</Button>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

IncomeCalculator.displayName = 'IncomeCalculator';

interface IIncomeCalculatorButtonProps {
    ref?: React.MutableRefObject<any>;
    onClickCallback: React.MouseEventHandler<HTMLDivElement>;
}

export const IncomeCalculatorButton = ({ 
    onClickCallback,
}: IIncomeCalculatorButtonProps) => (
    <div className='MonthlyIncome__incomeButton'
        onClick={onClickCallback}
        tabIndex={0}
        role='button'
        aria-labelledby='IncomeButtonText' >
        <Image className='MonthlyIncome__incomeButtonImage'
            src={CalculatorIcon}
            alt='Income Calculator Image' 
            loading='lazy' />
        <span id='IncomeButtonText' className='MonthlyIncome__incomeButtonText' >
                Calculate Your Monthly Income
        </span>
    </div>
);